import { Grid, Stack, useMediaQuery } from '@mui/material'
import React, { useState, useEffect } from 'react'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import {
  startOfMonth,
  getDay,
  getMonth,
  startOfISOWeek,
  subMonths,
  addMonths,
  addDays,
  subDays,
  getDaysInMonth,
  format,
  isWithinInterval,
} from 'date-fns'
import { useDispatch, useSelector } from 'react-redux'
import actionsService from '../../../services/actionsService'
import { localeDateFormat } from '../../../utils/localeUtils'
import theme from '../../../theme/theme'
import { ToggleButton, ToggleButtonGroup } from '../../../shared/UI/ToggleButton'
import {
  WallChartDay,
  WallChartWeek,
  WallChartMonth,
  WallChartSegments,
  WallChartTimespans,
  WallChartSelectedTimeSpan,
  WallChartSelectedTimespanProps,
} from './types'
import Paragraph from '../../../shared/UI/Paragraph'
import { MONTHS, WEEKDAYS } from '../../../utils/constants'
import { RootStore } from '../../../redux/store'
import {
  setLastSelectedDayTimespan,
  setLastSelectedMonthTimespan,
  setLastSelectedWeekTimespan,
  setSelectedTimespan,
  setWallChartSelected,
} from '../../../redux/reducers/wallChartReducer'
import { formatDateWithTimeZone } from '../../../utils/date-utils'
import FilterButton from '../../../shared/UI/FilterButton'

export function WallChartTimespanSelector({ onFilterClick }: WallChartSelectedTimespanProps) {
  const dispatch = useDispatch()
  const mobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [timespanStartDate, setTimespanStartDate] = useState<Date>(
    formatDateWithTimeZone(new Date())
  )

  const { selectedTimeSpan, lastSelectedDay, lastSelectedMonth, lastSelectedWeek, selectedParams } =
    useSelector((state: RootStore) => state.wallChart)

  const userPermissions = useSelector<RootStore, string[]>(
    (state: RootStore) => state.userState.permissions
  )

  const { calendarDetailsResponse } = useSelector((state: RootStore) => state.appSettings)

  useEffect(() => {
    setTimespanStartDate(selectedTimeSpan.startDate)
  }, [selectedTimeSpan])

  const getTimeSpanSegment = (
    timespan: WallChartTimespans,
    newDate?: Date
  ): WallChartDay | WallChartWeek | WallChartMonth => {
    if (timespan === WallChartTimespans.WEEK) {
      return {
        masterSegments: WallChartSegments.MASTER_SEGMENT_WEEK,
        divisions: WallChartSegments.SEGMENT_DIV_WEEK,
        timespan: WallChartTimespans.WEEK,
      }
    }
    if (timespan === WallChartTimespans.MONTH && newDate) {
      return {
        masterSegments: getDaysInMonth(newDate),
        divisions: WallChartSegments.SEGMENT_DIV_MONTH,
        timespan: WallChartTimespans.MONTH,
      }
    }
    return {
      masterSegments: WallChartSegments.MASTER_SEGMENT_DAY,
      divisions: WallChartSegments.SEGMENT_DIV_DAY,
      timespan: WallChartTimespans.DAY,
    }
  }

  const setLastSelected = (
    thisRangeType: string,
    timespan: WallChartSelectedTimeSpan | undefined
  ) => {
    switch (thisRangeType) {
      case WallChartTimespans.DAY:
        dispatch(setLastSelectedDayTimespan(timespan))
        break
      case WallChartTimespans.WEEK:
        dispatch(setLastSelectedWeekTimespan(timespan))
        break
      case WallChartTimespans.MONTH:
        dispatch(setLastSelectedMonthTimespan(timespan))
        break
      default:
        break
    }
  }

  const handleRangeChange = (
    _event: React.MouseEvent<HTMLElement> | undefined,
    newRangeType: WallChartTimespans
  ) => {
    if (!newRangeType) {
      return
    }
    actionsService.userAnalytics
      .postEvent('UIClickEvent', `WallChartTimespan${newRangeType}Click`, window.location.pathname, window.location.hostname)
      .catch((error: unknown) => console.error('Analytics tracking error:', error))
    const realToday = new Date()
    let timeSegment
    let timespanEndDate
    let title
    let newDate = timespanStartDate
    const isCurrentYear = selectedTimeSpan.startDate.getFullYear() === realToday.getFullYear()
    if (newRangeType === WallChartTimespans.DAY) {
      // When switching to Day view and the selected year is the current year,
      // anchor the view to the real current day; otherwise keep the existing selection.
      if (lastSelectedDay && newRangeType !== lastSelectedDay.rangeType) {
        return dispatch(setSelectedTimespan(lastSelectedDay))
      }

      if (isCurrentYear) {
        newDate = new Date(realToday.getFullYear(), realToday.getMonth(), realToday.getDate())
      } else {
        newDate = selectedTimeSpan.startDate
      }

      timespanEndDate = newDate
      timeSegment = getTimeSpanSegment(WallChartTimespans.DAY)
      title = WEEKDAYS[getDay(newDate)]
    } else if (newRangeType === WallChartTimespans.WEEK) {
      if (lastSelectedWeek && newRangeType !== lastSelectedWeek.rangeType) {
        // A previous week view was selected, so restore that
        return dispatch(setSelectedTimespan(lastSelectedWeek))
      }

      if (isCurrentYear) {
        const baseForWeek = lastSelectedDay ? lastSelectedDay.startDate : realToday
        const normalizedBase = new Date(
          baseForWeek.getFullYear(),
          baseForWeek.getMonth(),
          baseForWeek.getDate()
        )
        newDate = startOfISOWeek(normalizedBase)
      } else if (selectedTimeSpan.startDate.getFullYear() !== realToday.getFullYear()) {
        newDate = startOfISOWeek(newDate)
      } else {
        newDate = startOfISOWeek(startOfMonth(selectedTimeSpan.startDate))
      }
      timespanEndDate = addDays(newDate, 6)
      timeSegment = getTimeSpanSegment(WallChartTimespans.WEEK)
      title = WEEKDAYS[getDay(newDate)]
    } else {
      if (lastSelectedMonth && newRangeType !== lastSelectedMonth.rangeType) {
        // A previous month view was selected, so restore that
        return dispatch(setSelectedTimespan(lastSelectedMonth))
      }
      if (lastSelectedMonth && !isCurrentYear) {
        newDate = lastSelectedMonth.startDate
      } else {
        newDate = startOfMonth(timespanStartDate)
      }
      timespanEndDate = addDays(newDate, getDaysInMonth(newDate) - 1)
      timeSegment = getTimeSpanSegment(WallChartTimespans.MONTH, newDate)
      title = MONTHS[getMonth(newDate)]
    }
    setTimespanStartDate(newDate)
    const timespan = {
      title,
      directReport: selectedParams.directReport,
      rangeType: newRangeType,
      startDate: newDate,
      endDate: timespanEndDate,
      segments: timeSegment,
    }

    if (timespan.startDate.getHours() === 0) {
      timespan.startDate = formatDateWithTimeZone(timespan.startDate)
      timespan.endDate = formatDateWithTimeZone(timespan.endDate)
    }

    dispatch(setSelectedTimespan(timespan))
    setLastSelected(newRangeType, timespan)
  }

  const handleViewAdvanceClick = (
    _event: React.MouseEvent<HTMLElement>,
    newViewSelected: string
  ) => {
    if (!newViewSelected) {
      return
    }
    const direction = newViewSelected === 'Inc' ? 'Next' : 'Previous'
    actionsService.userAnalytics
      .postEvent('UIClickEvent', `WallChartNavigation${direction}Click`, window.location.pathname, window.location.hostname)
      .catch((error: unknown) => console.error('Analytics tracking error:', error))
    let timeSegment
    let timespanEndDate
    let title
    let newDate = timespanStartDate
    if (selectedTimeSpan.rangeType === WallChartTimespans.DAY) {
      newDate =
        newViewSelected === 'Inc' ? addDays(timespanStartDate, 1) : subDays(timespanStartDate, 1)
      timespanEndDate = newDate
      timeSegment = getTimeSpanSegment(WallChartTimespans.DAY)
      title = WEEKDAYS[getDay(newDate)]
      setLastSelected(WallChartTimespans.WEEK, undefined)
    } else if (selectedTimeSpan.rangeType === WallChartTimespans.WEEK) {
      newDate =
        newViewSelected === 'Inc' ? addDays(timespanStartDate, 7) : subDays(timespanStartDate, 7)
      timespanEndDate = addDays(newDate, 6)
      timeSegment = getTimeSpanSegment(WallChartTimespans.WEEK)
      title = WEEKDAYS[getDay(newDate)]
    } else {
      // MONTH
      const beginningOfMonth = startOfMonth(timespanStartDate)
      newDate =
        newViewSelected === 'Inc' ? addMonths(beginningOfMonth, 1) : subMonths(beginningOfMonth, 1)
      timespanEndDate = addDays(newDate, getDaysInMonth(newDate) - 1)
      timeSegment = getTimeSpanSegment(WallChartTimespans.MONTH, newDate)
      title = MONTHS[getMonth(newDate)]
      setLastSelected(WallChartTimespans.WEEK, undefined)
    }
    setTimespanStartDate(newDate)
    const timespan = {
      title,
      rangeType: selectedTimeSpan.rangeType,
      directReport: selectedParams.directReport,
      startDate: newDate,
      endDate: timespanEndDate,
      segments: timeSegment,
    }
    
    if (timespan.startDate.getHours() === 0) {
      timespan.startDate = formatDateWithTimeZone(timespan.startDate)
      timespan.endDate = formatDateWithTimeZone(timespan.endDate)
    }

    dispatch(setSelectedTimespan(timespan))
    setLastSelected(selectedTimeSpan.rangeType, timespan)
    if (newDate.getFullYear() !== selectedTimeSpan.startDate.getFullYear()) {
      dispatch(
        setWallChartSelected({
          ...selectedParams,
          selectedYear: {
            value: newDate.getFullYear(),
            displayValue: String(newDate.getFullYear()),
          },
        })
      )
    }
  }

  const mediaQueryWidth = useMediaQuery(theme.breakpoints.down('xl'))

  const checkValidYearLimit = (type: string) => {
    if (!calendarDetailsResponse) {
      return
    }

    const lastBookableDate = new Date(
      calendarDetailsResponse.calendarDetails[
        calendarDetailsResponse.calendarDetails.length - 1
      ].endDate
    )

    const selectedEndDate = new Date(selectedTimeSpan.endDate)

    const selectedStartDate = new Date(selectedTimeSpan.startDate)

    switch (type) {
      case 'inc':
        if (
          isWithinInterval(lastBookableDate, {
            start: selectedStartDate,
            end: new Date(format(addDays(selectedEndDate, 1), 'yyyy-MM-dd')),
          })
        ) {
          return true
        }
        return (
          format(new Date(selectedTimeSpan.endDate), `yyyy-MM-dd`) >=
          format(
            new Date(
              calendarDetailsResponse.calendarDetails[
                calendarDetailsResponse.calendarDetails.length - 1
              ].endDate
            ),
            `yyyy-MM-dd`
          )
        )
      case 'dec':
        return (
          format(new Date(selectedTimeSpan.startDate), `yyyy-MM-dd`) <=
          format(new Date(calendarDetailsResponse.calendarDetails[0].startDate), `yyyy-MM-dd`)
        )
      default:
        return false
    }
  }

  return (
    <Grid container display="flex" justifyContent="space-between" alignItems="flex-start" rowSpacing={1}>
      <Grid item display="flex" alignItems="center" xs={12} md={4}>
        <Stack direction="row" justifyContent="flex-start" alignItems="center" columnGap={2}>
          <ToggleButtonGroup
            value={null}
            exclusive
            bgColor={theme.palette.primary.main}
            onChange={handleViewAdvanceClick}
            disabled={!selectedTimeSpan}
          >
            <ToggleButton value="Dec" fixedColor="white" disabled={checkValidYearLimit('dec')}>
              <ChevronLeftIcon />
            </ToggleButton>
            <ToggleButton value="Inc" fixedColor="white" disabled={checkValidYearLimit('inc')}>
              <ChevronRightIcon />
            </ToggleButton>
          </ToggleButtonGroup>
          <Paragraph weight="bold">
            {selectedTimeSpan.title}
            {selectedTimeSpan.segments.timespan === WallChartTimespans.WEEK && ' WC:'}
          </Paragraph>
          {selectedTimeSpan.segments.timespan === WallChartTimespans.DAY && (
            <Paragraph>{format(timespanStartDate, localeDateFormat)}</Paragraph>
          )}
          {selectedTimeSpan.segments.timespan === WallChartTimespans.WEEK && (
            <Paragraph>{format(timespanStartDate, localeDateFormat)}</Paragraph>
          )}
        </Stack>
      </Grid>
      <Grid
        item
        xs={6}
        md={4}
        display="flex"
        alignItems="center"
        justifyContent={{ xs: "flex-start", md: "center" }}
      >
        <ToggleButtonGroup
          value={selectedTimeSpan.rangeType}
          exclusive
          bgColor={theme.palette.primary.main}
          onChange={handleRangeChange}
          disabled={!selectedTimeSpan}
          minWidth={mediaQueryWidth ? '75px' : '112px'}
        >
          <ToggleButton value={WallChartTimespans.DAY} data-testid="WallChart-DayFilter">
            {WallChartTimespans.DAY}
          </ToggleButton>
          <ToggleButton value={WallChartTimespans.WEEK} data-testid="WallChart-WeekFilter">
            {WallChartTimespans.WEEK}
          </ToggleButton>
          <ToggleButton value={WallChartTimespans.MONTH} data-testid="WallChart-MonthFilter">
            {WallChartTimespans.MONTH}
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>
      <Grid item xs={6} md={4} display="flex" justifyContent="flex-end">
        <FilterButton
          onClick={(event: unknown) => {
            actionsService.userAnalytics
              .postEvent('UIClickEvent', 'WallChartFilterButtonClick', window.location.pathname, window.location.hostname)
              .catch((error: unknown) => console.error('Analytics tracking error:', error))
            onFilterClick(event)
          }}
        />
      </Grid>
    </Grid>
  )
}
